import { yupResolver } from '@hookform/resolvers/yup'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'
import { uploadImages } from '@/core/services/upload-image'
import { argbToCssHex, cssHexToArgb } from '@/lib/color-format'
import {
  PartnerDto,
  ROADMAP_SCOPES,
  RoadmapDto,
  RoadmapScope,
  RoadmapTranslationDto,
  RoadmapUpsertDto,
} from '@/common/models/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { ColorField } from '../settings/settings-components/ColorField'
import { GradientField } from '../settings/settings-components/GradientField'
import { TranslationTabs } from './shared/TranslationTabs'

interface CreateRoadmapModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roadmap?: RoadmapDto | null
  partners: PartnerDto[]
  onSubmit: (values: RoadmapUpsertDto, id?: string) => void
  onDelete?: (id: string) => void
}

interface IRoadmapFormValues {
  scope: RoadmapScope
  partnerId?: string
  logoRef?: string
  ordinal: number
  active: boolean
  translations: RoadmapTranslationDto[]
  themeEnabled: boolean
  /**
   * Form/UI state stays in CSS hex (`#RRGGBB[AA]`, alpha last) — the format
   * `ColorField`/`GradientField` render and the format CSS gradients expect.
   * Converted to/from the API's `#AARRGGBB` (alpha first) only at load and
   * submit via `argbToCssHex`/`cssHexToArgb`.
   * The main colour plus two gradients (GRADIENT_1, GRADIENT_2), each with its
   * own fall colour. Field names (`from`/`to`/`fallBackColor`) are forced by
   * `GradientField`.
   */
  theme: {
    mainColor: string
    gradient1: ThemeGradient
    gradient2: ThemeGradient
  }
}

interface ThemeGradient {
  from: string
  to: string
  fallBackColor: string
}

const DEFAULT_THEME_COLOR = '#FFFFFF'
/** Fresh instance each call — gradient1 and gradient2 must not share a reference. */
const emptyGradient = (): ThemeGradient => ({
  from: DEFAULT_THEME_COLOR,
  to: DEFAULT_THEME_COLOR,
  fallBackColor: DEFAULT_THEME_COLOR,
})

const SCOPE_LABELS: Record<RoadmapScope, string> = {
  FESTIVAL: 'Festival',
  PARTNER: 'Partner',
}

export const CreateRoadmapModal = ({
  open,
  onOpenChange,
  roadmap,
  partners,
  onSubmit,
  onDelete,
}: CreateRoadmapModalProps) => {
  const isEditMode = !!roadmap
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | undefined>()

  const COLOR_HEX_REGEX = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/

  // Required + format-checked only while `themeEnabled` is true (mirrors the
  // server-side rule); hoisted to the `theme` object level (rather than
  // per-field `.when('themeEnabled', ...)`) because yup siblings resolve
  // relative to the object being validated, and `themeEnabled` lives one
  // level up from `theme.*`.
  const colorSchema = yup
    .string()
    .required('Color is required')
    .matches(COLOR_HEX_REGEX, 'Invalid color')
  const gradientSchema = yup.object().shape({
    from: colorSchema,
    to: colorSchema,
    fallBackColor: colorSchema,
  })
  const themeColorSchema = yup.object().shape({
    mainColor: colorSchema,
    gradient1: gradientSchema,
    gradient2: gradientSchema,
  })

  const schema = yup.object().shape({
    scope: yup
      .string()
      .oneOf(ROADMAP_SCOPES, 'Scope is required')
      .required('Scope is required'),
    partnerId: yup.string().when('scope', {
      is: 'PARTNER',
      then: (s) => s.required('Partner is required'),
      otherwise: (s) => s.notRequired(),
    }),
    logoRef: yup.string().optional(),
    ordinal: yup.number().typeError('Order must be a number').min(0).default(0),
    active: yup.boolean().default(true),
    translations: yup.array().default([]),
    themeEnabled: yup.boolean().default(false),
    theme: yup.object().when('themeEnabled', {
      is: true,
      then: () => themeColorSchema,
      otherwise: (s) => s.notRequired(),
    }),
  })

  const defaultValues: IRoadmapFormValues = {
    scope: 'FESTIVAL',
    partnerId: undefined,
    logoRef: undefined,
    ordinal: 0,
    active: true,
    translations: [],
    themeEnabled: false,
    theme: {
      mainColor: DEFAULT_THEME_COLOR,
      gradient1: emptyGradient(),
      gradient2: emptyGradient(),
    },
  }

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<IRoadmapFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues,
  })

  const scope = watch('scope')
  const logoRef = watch('logoRef')
  const themeEnabled = watch('themeEnabled')
  const gradient1From = watch('theme.gradient1.from')
  const gradient1To = watch('theme.gradient1.to')
  const gradient2From = watch('theme.gradient2.from')
  const gradient2To = watch('theme.gradient2.to')

  const handleLogoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1_000_000) {
      setUploadError('The logo is too large (max 1 MB)')
      return
    }
    setUploadError(undefined)
    setIsUploading(true)
    try {
      const { data } = await uploadImages(
        file,
        true,
        'GAMIFICATION_ROADMAP_LOGO',
      )
      if (data?.photoUrl) {
        setValue('logoRef', data.photoUrl, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      setUploadError(
        status === 400
          ? 'Logo must be at least 300px wide'
          : 'Logo upload failed',
      )
    } finally {
      setIsUploading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    if (roadmap) {
      setValue('scope', roadmap.scope, { shouldValidate: true })
      setValue('partnerId', roadmap.partnerId ?? undefined, {
        shouldValidate: true,
      })
      setValue('logoRef', roadmap.logoRef ?? undefined, {
        shouldValidate: true,
      })
      setValue('ordinal', roadmap.ordinal ?? 0, { shouldValidate: true })
      setValue('active', roadmap.active, { shouldValidate: true })
      setValue('translations', roadmap.translations ?? [], {
        shouldValidate: true,
      })
      setValue('themeEnabled', !!roadmap.theme, { shouldValidate: true })
      setValue(
        'theme',
        {
          mainColor:
            argbToCssHex(roadmap.theme?.mainColor) ?? DEFAULT_THEME_COLOR,
          gradient1: {
            from:
              argbToCssHex(roadmap.theme?.gradient1FromColor) ??
              DEFAULT_THEME_COLOR,
            to:
              argbToCssHex(roadmap.theme?.gradient1ToColor) ??
              DEFAULT_THEME_COLOR,
            fallBackColor:
              argbToCssHex(roadmap.theme?.gradient1FallbackColor) ??
              DEFAULT_THEME_COLOR,
          },
          gradient2: {
            from:
              argbToCssHex(roadmap.theme?.gradient2FromColor) ??
              DEFAULT_THEME_COLOR,
            to:
              argbToCssHex(roadmap.theme?.gradient2ToColor) ??
              DEFAULT_THEME_COLOR,
            fallBackColor:
              argbToCssHex(roadmap.theme?.gradient2FallbackColor) ??
              DEFAULT_THEME_COLOR,
          },
        },
        { shouldValidate: true },
      )
    }
    return () => {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roadmap])

  const handleClose = () => onOpenChange(false)

  const submit = (values: IRoadmapFormValues) => {
    onSubmit(
      {
        scope: values.scope,
        partnerId: values.scope === 'PARTNER' ? values.partnerId : undefined,
        logoRef: values.logoRef || undefined,
        active: values.active,
        ordinal: values.ordinal,
        translations: values.translations,
        // Explicit `null` (not `undefined`) when disabled, so the clear
        // path reaches the server rather than being dropped from the payload.
        theme: values.themeEnabled
          ? {
              mainColor: cssHexToArgb(values.theme.mainColor) ?? '',
              gradient1FromColor:
                cssHexToArgb(values.theme.gradient1.from) ?? '',
              gradient1ToColor: cssHexToArgb(values.theme.gradient1.to) ?? '',
              gradient1FallbackColor:
                cssHexToArgb(values.theme.gradient1.fallBackColor) ?? '',
              gradient2FromColor:
                cssHexToArgb(values.theme.gradient2.from) ?? '',
              gradient2ToColor: cssHexToArgb(values.theme.gradient2.to) ?? '',
              gradient2FallbackColor:
                cssHexToArgb(values.theme.gradient2.fallBackColor) ?? '',
            }
          : null,
      },
      roadmap?.id,
    )
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="flash"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Roadmap</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode && onDelete ? (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  (isEditMode && Object.keys(dirtyFields).length === 0) ||
                  isUploading
                }
                form="create-roadmap-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-roadmap-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <Controller
            name="scope"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Scope"
                required
                placeholder="Select scope"
                error={errors?.scope?.message}
                options={ROADMAP_SCOPES.map((s) => ({
                  value: s,
                  label: SCOPE_LABELS[s],
                }))}
                onValueChange={field.onChange}
                {...field}
              />
            )}
          />

          {scope === 'PARTNER' && (
            <Controller
              name="partnerId"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Partner"
                  required
                  placeholder="Select partner"
                  error={errors?.partnerId?.message}
                  options={partners.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  onValueChange={field.onChange}
                  {...field}
                />
              )}
            />
          )}

          <div>
            <FormUploadPreview
              label="Logo (optional)"
              previewLabel="Logo"
              isUploading={isUploading}
              url={logoRef}
              width={60}
              height={60}
              imageName={logoRef ? 'roadmap logo' : undefined}
              onRemove={() =>
                setValue('logoRef', undefined, { shouldDirty: true })
              }
              imageStyle={{ objectFit: 'contain' }}
              control={
                <Input
                  multiple={false}
                  accept="image/*"
                  isInvalid={!!uploadError}
                  type="file"
                  className="bg-monochrome-5-50"
                  onChange={handleLogoUpload}
                />
              }
            />
            {uploadError && (
              <span className="text-xs text-accent-3">{uploadError}</span>
            )}
          </div>

          <Controller
            name="themeEnabled"
            control={control}
            render={({ field }) => (
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(!!checked)
                    if (checked) {
                      setValue(
                        'theme',
                        {
                          mainColor: DEFAULT_THEME_COLOR,
                          gradient1: emptyGradient(),
                          gradient2: emptyGradient(),
                        },
                        { shouldValidate: true, shouldDirty: true },
                      )
                    }
                  }}
                />
                <span className="text-body-medium text-monochrome-1">
                  Custom theme
                </span>
              </label>
            )}
          />

          {themeEnabled && (
            <>
              <ColorField
                control={control}
                name="theme.mainColor"
                label="Main Color"
                error={errors?.theme?.mainColor?.message}
              />
              <GradientField
                control={control}
                baseName="theme.gradient1"
                label="Gradient 1"
                fromValue={gradient1From}
                toValue={gradient1To}
                fromError={errors?.theme?.gradient1?.from?.message}
                toError={errors?.theme?.gradient1?.to?.message}
                fallError={errors?.theme?.gradient1?.fallBackColor?.message}
              />
              <GradientField
                control={control}
                baseName="theme.gradient2"
                label="Gradient 2"
                fromValue={gradient2From}
                toValue={gradient2To}
                fromError={errors?.theme?.gradient2?.from?.message}
                toError={errors?.theme?.gradient2?.to?.message}
                fallError={errors?.theme?.gradient2?.fallBackColor?.message}
              />
            </>
          )}

          <FormGroup label="Order" error={errors?.ordinal?.message}>
            <Input
              isInvalid={!!errors?.ordinal?.message}
              {...register('ordinal', { valueAsNumber: true })}
              type="number"
              placeholder="0"
            />
          </FormGroup>

          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(!!checked)}
                />
                <span className="text-body-medium text-monochrome-1">
                  Active
                </span>
              </label>
            )}
          />

          <Controller
            name="translations"
            control={control}
            render={({ field }) => (
              <TranslationTabs<RoadmapTranslationDto>
                value={field.value}
                onChange={field.onChange}
                makeEmpty={(locale) => ({ locale, title: '', subtitle: '' })}
                renderFields={({ value, onFieldChange }) => (
                  <div className="space-y-4">
                    <FormGroup label="Title">
                      <Input
                        value={value.title ?? ''}
                        onChange={(e) =>
                          onFieldChange({ title: e.target.value })
                        }
                        type="text"
                        placeholder="Enter title"
                      />
                    </FormGroup>
                    <FormGroup label="Subtitle">
                      <Input
                        value={value.subtitle ?? ''}
                        onChange={(e) =>
                          onFieldChange({ subtitle: e.target.value })
                        }
                        type="text"
                        placeholder="Enter subtitle"
                      />
                    </FormGroup>
                  </div>
                )}
              />
            )}
          />
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deletedItem="roadmap"
        deleteFunc={() => {
          if (roadmap && onDelete) onDelete(roadmap.id)
          handleClose()
        }}
      />
    </>
  )
}
